import { useEffect, useRef } from 'react';
import { EventSourceMessage } from '@microsoft/fetch-event-source';
import { reportException } from '@/services/sentry.ts';

import useRequiredAuthInfo from '@/hooks/useRequiredAuthInfo';

import {
  AutomationEventTypes,
  EventPayload,
  RunEventTypes,
  ServerSideEventType,
} from '@/hooks/api/sse/types';
import { useSSEConnection } from '@/hooks/api/sse/useSSEConnection.ts';

function isServerSideEventType(value: string): value is ServerSideEventType {
  return (
    Object.values(AutomationEventTypes).includes(value as AutomationEventTypes) ||
    Object.values(RunEventTypes).includes(value as RunEventTypes)
  );
}

export function useBaseSSE(
  eventTypes: string[],
  onEvent: (type: ServerSideEventType, payload: EventPayload) => void,
  onConnect: () => void,
  url: string,
  enabled: boolean,
) {
  const { tokens, refreshAuthInfo } = useRequiredAuthInfo();

  const eventTypesRef = useRef(eventTypes);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    eventTypesRef.current = eventTypes;
  }, [eventTypes]);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const { isConnected, isDisabled } = useSSEConnection({
    url,
    getAccessToken: () => tokens.getAccessToken(),
    onUnauthorized: () => refreshAuthInfo(),
    onConnect,
    onMessage: (msg: EventSourceMessage) => {
      const type = msg.event ?? 'message';

      if (type === 'heartbeat' || type === 'connected' || type === 'message') return;

      const allowed = eventTypesRef.current;
      if (allowed.length > 0 && !allowed.includes(type)) return;
      if (!isServerSideEventType(type)) return;
      if (!msg.data) return;

      try {
        const payload = JSON.parse(msg.data) as EventPayload;
        onEventRef.current(type, payload);
      } catch (e) {
        reportException(e, { extra: { phase: 'parse', type, url } });
      }
    },
    enabled,
  });

  return { isConnected, isDisabled };
}
